import { z } from "zod";
import { LlmRequestSchema, LlmResponseSchema, type LlmClient, type LlmRequest, type LlmResponse } from "./llm.js";

/**
 * Anthropic-backed LlmClient. Reads `ANTHROPIC_API_KEY` from env.
 *
 * This implementation lives in `@keystone/core` (alongside `LlmClient`) so every
 * package that wants live access can import it without taking a wider workspace
 * dependency. It's intentionally lazy-loaded — calling `complete()` triggers a
 * dynamic import of `@anthropic-ai/sdk`, so packages that never use the live path
 * (CI replay, offline demo) don't carry the SDK at runtime.
 *
 * Determinism note (operating rule 4): tests must never instantiate this client.
 * They use `RecordedLlmClient` against committed fixtures. The factory below
 * refuses to construct if `NODE_ENV === "test"` unless explicitly overridden.
 */
export interface AnthropicLlmClientOptions {
  apiKey?: string;
  defaultModel?: string;
  /** Escape hatch for test runners that genuinely need live calls (recording). */
  allowInTest?: boolean;
}

export const AnthropicToolCallSchema = z.object({
  name: z.string(),
  args: z.unknown()
});

export class AnthropicLlmClient implements LlmClient {
  private readonly apiKey: string;
  private readonly defaultModel: string;

  constructor(opts: AnthropicLlmClientOptions = {}) {
    if (process.env.NODE_ENV === "test" && !opts.allowInTest) {
      throw new Error(
        "AnthropicLlmClient instantiated in test environment. Use RecordedLlmClient with committed fixtures."
      );
    }
    const key = opts.apiKey ?? process.env.ANTHROPIC_API_KEY;
    if (!key) {
      throw new Error(
        "ANTHROPIC_API_KEY not set. Either provide opts.apiKey or set the env var (live mode only)."
      );
    }
    this.apiKey = key;
    this.defaultModel = opts.defaultModel ?? "claude-opus-4-7";
  }

  async complete(rawReq: LlmRequest): Promise<LlmResponse> {
    const req = LlmRequestSchema.parse(rawReq);
    // Dynamic import keeps the SDK out of the cold path for replay-only consumers.
    const mod = await import("@anthropic-ai/sdk");
    const Anthropic = (mod as { default?: unknown }).default ?? mod;
    const client = new (Anthropic as new (cfg: { apiKey: string }) => {
      messages: {
        create: (cfg: Record<string, unknown>) => Promise<Record<string, unknown>>;
      };
    })({ apiKey: this.apiKey });

    // Translate Keystone messages → Anthropic content-block shape.
    //  - System messages split off into the top-level `system` param.
    //  - LlmEvalAgent encodes assistant tool calls as JSON strings of shape
    //    `{ tool_call: { name, args } }`. Detect that and emit a real
    //    `tool_use` content block so the model sees structured calls.
    //  - The same agent encodes tool-role results as JSON strings of shape
    //    `{ tool, result }`. Emit those as `tool_use_id`-paired `tool_result`
    //    user content blocks.
    const system = req.messages.filter((m) => m.role === "system").map((m) => m.content).join("\n\n");
    type ContentBlock =
      | { type: "text"; text: string }
      | { type: "tool_use"; id: string; name: string; input: unknown }
      | { type: "tool_result"; tool_use_id: string; content: string };
    type AnthropicTurn = { role: "user" | "assistant"; content: ContentBlock[] };

    const turns: AnthropicTurn[] = [];
    let nextToolUseId = 0;
    let pendingToolUseId: string | undefined;

    for (const m of req.messages) {
      if (m.role === "system") continue;
      const role: "user" | "assistant" = m.role === "assistant" ? "assistant" : "user";
      // Try to parse JSON-encoded structured messages.
      interface StructuredMsg {
        tool_call?: { name: string; args: unknown };
        tool?: string;
        result?: unknown;
      }
      let parsed: StructuredMsg | null = null;
      try {
        const p = JSON.parse(m.content) as unknown;
        if (p && typeof p === "object") parsed = p as StructuredMsg;
      } catch {
        parsed = null;
      }
      if (role === "assistant" && parsed && parsed.tool_call) {
        const id = `t${nextToolUseId++}`;
        pendingToolUseId = id;
        turns.push({
          role: "assistant",
          content: [
            { type: "tool_use", id, name: parsed.tool_call.name, input: parsed.tool_call.args }
          ]
        });
        continue;
      }
      if (m.role === "tool" && parsed && parsed.tool !== undefined && pendingToolUseId) {
        turns.push({
          role: "user",
          content: [
            { type: "tool_result", tool_use_id: pendingToolUseId, content: JSON.stringify(parsed.result ?? {}) }
          ]
        });
        pendingToolUseId = undefined;
        continue;
      }
      // Default: text content.
      turns.push({ role, content: [{ type: "text", text: m.content }] });
    }

    // Translate the tool catalog.
    const tools = (req.tools ?? []).map((t) => ({
      name: t.name,
      description: t.description,
      input_schema:
        // Anthropic requires a valid JSON Schema with `type: "object"` at the
        // root. Most of our tools already conform; fall back to a permissive
        // shape if the design carried something else.
        isJsonSchemaObject(t.inputSchema)
          ? t.inputSchema
          : { type: "object", properties: {}, additionalProperties: true }
    }));

    const response = (await client.messages.create({
      model: req.model || this.defaultModel,
      max_tokens: req.maxTokens,
      temperature: req.temperature,
      ...(system ? { system } : {}),
      ...(tools.length > 0 ? { tools } : {}),
      messages: turns
    })) as {
      content: Array<{ type: string; text?: string; name?: string; input?: unknown }>;
      usage?: { input_tokens?: number; output_tokens?: number };
    };

    const text = response.content
      .filter((b) => b.type === "text")
      .map((b) => b.text ?? "")
      .join("\n");
    const toolCalls = response.content
      .filter((b) => b.type === "tool_use")
      .map((b) => ({ name: b.name ?? "", args: b.input }));

    return LlmResponseSchema.parse({
      text,
      toolCalls,
      usage: response.usage
        ? {
            inputTokens: response.usage.input_tokens ?? 0,
            outputTokens: response.usage.output_tokens ?? 0
          }
        : undefined
    });
  }
}

function isJsonSchemaObject(s: unknown): s is { type: "object"; [k: string]: unknown } {
  if (s === null || typeof s !== "object") return false;
  const o = s as Record<string, unknown>;
  return o.type === "object";
}

/**
 * RecordingLlmClient — wraps another `LlmClient` and writes every (canonical key
 * digest, response) pair to memory; `dump()` returns them in a stable order for
 * commit as a fixture. The recording can then be loaded with
 * `RecordedLlmClient.fromArray(...)` for deterministic replay in CI.
 */
export class RecordingLlmClient implements LlmClient {
  private readonly entries: Array<{ keyHash: string; request: LlmRequest; response: LlmResponse }> = [];

  constructor(private readonly inner: LlmClient) {}

  async complete(req: LlmRequest): Promise<LlmResponse> {
    const { canonicalSha256 } = await import("./digest.js");
    const keyHash = canonicalSha256(req);
    const response = await this.inner.complete(req);
    this.entries.push({ keyHash, request: req, response });
    return response;
  }

  dump(): Array<{ keyHash: string; request: LlmRequest; response: LlmResponse }> {
    // Sort by keyHash so the committed fixture order is stable independent of
    // call sequence — important for git diff sanity.
    return [...this.entries].sort((a, b) => a.keyHash.localeCompare(b.keyHash));
  }
}
